"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState, useTransition } from "react";

import { LogOutIcon, SearchIcon, SettingsIcon, UserPlusIcon, type LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";

import { useSettingsDialog } from "@/app/(app)/_components/settings-dialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { isSingleLink, visibleNav, type NavTitle } from "@/config/nav";
import { THEMES } from "@/config/themes";
import { useCurrentUser } from "@/features/auth/components/current-user-provider";
import { signOut } from "@/features/auth/sign-out";
import { userListHref } from "@/features/users/search-params";

type PageLink = { title: NavTitle; href: string; icon: LucideIcon; group?: NavTitle };

/** ⌘K / Ctrl+K: jump to any page you can see, or run a quick action. */
export function CommandMenu() {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const { permissions } = useCurrentUser();
  const { setTheme } = useTheme();
  const settings = useSettingsDialog();
  const [, startTransition] = useTransition();
  const t = useTranslations();

  // A keyboard shortcut is an external event source: the one thing effects are for
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  /** Close first, so focus doesn't land back on the trigger after navigating. */
  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  // navigation.md rule 14: the same entries the sidebar shows
  const pages = visibleNav(permissions).flatMap((section): PageLink[] =>
    isSingleLink(section)
      ? section.entries.map((entry) => ({
          title: section.title,
          href: entry.href,
          icon: section.icon,
        }))
      : section.entries.map((entry) => ({
          title: entry.title,
          href: entry.href,
          icon: section.icon,
          group: section.title,
        })),
  );

  return (
    <>
      {/* Looks like a search field: wide on larger screens, an icon on phones */}
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Meta+K Control+K"
        className="size-9 p-0 text-muted-foreground sm:h-9 sm:w-64 sm:justify-start sm:bg-muted/40 sm:px-3 sm:font-normal lg:w-80"
      >
        <SearchIcon aria-hidden />
        <span className="sr-only sm:not-sr-only">{t("shell.search")}</span>
        <kbd className="pointer-events-none ml-auto hidden h-5 items-center gap-0.5 rounded border bg-background px-1.5 font-mono text-[11px] font-medium sm:inline-flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t("shell.searchTitle")}
        description={t("shell.searchDescription")}
      >
        {/* shadcn's CommandDialog no longer adds the cmdk root itself */}
        <Command>
          <CommandInput placeholder={t("shell.searchPlaceholder")} />
          <CommandList>
            <CommandEmpty>{t("shell.nothingFound")}</CommandEmpty>
            <CommandGroup heading={t("shell.pages")}>
              {pages.map((page) => (
                <CommandItem
                  key={page.href + page.title}
                  value={[page.group && t(`nav.${page.group}`), t(`nav.${page.title}`)]
                    .filter(Boolean)
                    .join(" ")}
                  onSelect={run(() => router.push(page.href))}
                >
                  <page.icon aria-hidden />
                  {page.group
                    ? `${t(`nav.${page.group}`)}: ${t(`nav.${page.title}`)}`
                    : t(`nav.${page.title}`)}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading={t("shell.commandActions")}>
              {permissions.includes("user:manage") && (
                <CommandItem onSelect={run(() => router.push(userListHref({ invite: true })))}>
                  <UserPlusIcon aria-hidden />
                  {t("shell.inviteUser")}
                </CommandItem>
              )}
              <CommandItem onSelect={run(() => void settings.open())}>
                <SettingsIcon aria-hidden />
                {t("shell.settings")}
              </CommandItem>
              {THEMES.map(({ value, icon: Icon }) => (
                <CommandItem
                  key={value}
                  value={t("shell.themeOption", { theme: t(`themes.${value}`) })}
                  onSelect={run(() => setTheme(value))}
                >
                  <Icon aria-hidden />
                  {t("shell.themeOption", { theme: t(`themes.${value}`) })}
                </CommandItem>
              ))}
              <CommandItem onSelect={run(() => startTransition(() => signOut()))}>
                <LogOutIcon aria-hidden />
                {t("shell.signOut")}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
